/**
 * ServiceDesignerModelApplyModal — apply a flexible model/template to the
 * current service (spec §3, §15, §17.5).
 *
 * - Lists active models from /api/service-designer/models
 * - For each model node (role) the user binds an EXISTING CI (search, no dup)
 * - POST /models/<id>/apply → seeds members + typed relations; never locks
 */
import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import './ServiceDesigner.css';

const DEFAULT_TYPES = [
  'rack_element', 'virt_vm', 'printer', 'wifi_controller', 'wifi_access_point',
  'firewall', 'router', 'workstation', 'device', 'storage_disk', 'isp_circuit', 'service',
];

const TYPE_LABELS = {
  rack_element: 'Servidor', virt_vm: 'VM', printer: 'Impresora',
  wifi_controller: 'Controladora WiFi', wifi_access_point: 'AP WiFi', firewall: 'Firewall',
  router: 'Router', workstation: 'Puesto', device: 'Dispositivo', storage_disk: 'Disco',
  isp_circuit: 'Circuito ISP', service: 'Servicio', ipam_ip: 'IP',
};

const TYPE_COLORS = {
  service: '#7c3aed', rack_element: '#2563eb', virt_vm: '#0d9488',
  printer: '#b45309', firewall: '#dc2626', router: '#7c3aed', workstation: '#4b5563',
  device: '#6b7280', storage_disk: '#0891b2', isp_circuit: '#65a30d', ipam_ip: '#475569',
};

export default function ServiceDesignerModelApplyModal({ companyId, serviceId, serviceName, onClose, onApplied }) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(null);            // modelo seleccionado
  const [bindings, setBindings] = useState({});    // role -> {ci_type, ci_id, name}
  const [queries, setQueries] = useState({});      // role -> texto
  const [results, setResults] = useState({});      // role -> [ {ci_type, ci_id, name} ]
  const [searching, setSearching] = useState({});  // role -> bool
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.sdListModels(companyId);
      setModels(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const selModel = models.find((m) => String(m.id) === String(sel)) || null;

  const searchRole = async (role, node, q) => {
    setQueries((p) => ({ ...p, [role]: q }));
    if (!q || q.trim().length < 2) {
      setResults((p) => ({ ...p, [role]: [] }));
      return;
    }
    setSearching((p) => ({ ...p, [role]: true }));
    try {
      const types = node.ci_type ? [node.ci_type] : DEFAULT_TYPES;
      const res = await api.sdSearchCIs(companyId, types, q.trim(), 8);
      setResults((p) => ({
        ...p,
        [role]: (res.data || []).map((r) => ({
          ci_type: r.ci_type, ci_id: r.ci_id, name: r.name || `${TYPE_LABELS[r.ci_type] || r.ci_type} #${r.ci_id}`,
        })),
      }));
    } catch (_) {
      setResults((p) => ({ ...p, [role]: [] }));
    } finally {
      setSearching((p) => ({ ...p, [role]: false }));
    }
  };

  const pickCi = (role, ci) => {
    setBindings((p) => ({ ...p, [role]: ci }));
    setQueries((p) => ({ ...p, [role]: ci.name }));
    setResults((p) => ({ ...p, [role]: [] }));
  };

  const clearCi = (role) => {
    setBindings((p) => { const n = { ...p }; delete n[role]; return n; });
    setQueries((p) => ({ ...p, [role]: '' }));
  };

  const apply = async () => {
    if (!selModel) return;
    const roleBindings = {};
    Object.entries(bindings).forEach(([role, ci]) => {
      if (ci && ci.ci_type && ci.ci_id) roleBindings[role] = { ci_type: ci.ci_type, ci_id: ci.ci_id };
    });
    setApplying(true);
    setError(null);
    try {
      const res = await api.sdApplyModel(serviceId, selModel.id, companyId, roleBindings);
      if (onApplied) onApplied(res.data);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="sd-modal-backdrop" onClick={onClose}>
      <div className="sd-modal sd-model-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sd-model-head">
          <h3>🧩 Aplicar plantilla</h3>
          <button className="sd-model-x" onClick={onClose} title="Cerrar"><XMarkIcon width={16} height={16} /></button>
        </div>
        <p className="sd-modal-sub">
          Servicio: <strong>{serviceName}</strong>. Las plantillas siembran miembros y relaciones;
          nunca bloquean lo que añadas después.
        </p>

        {error && <div className="sd-error-banner">{error}<button onClick={() => setError(null)}><XMarkIcon width={14} height={14} /></button></div>}

        {loading ? (
          <p className="sd-hint">Cargando plantillas…</p>
        ) : models.length === 0 ? (
          <div className="sd-empty">No hay plantillas creadas todavía. Créalas desde «Servicios → Diseñador → Plantillas».</div>
        ) : (
          <>
            <select
              className="sd-model-select"
              value={sel || ''}
              onChange={(e) => { setSel(e.target.value); setBindings({}); setQueries({}); setResults({}); }}
            >
              <option value="">— Elige una plantilla —</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} · {(m.nodes || []).length} roles / {(m.relations || []).length} relaciones
                </option>
              ))}
            </select>

            {selModel && (
              <div className="sd-model-roles">
                <p className="sd-model-roles-title">
                  {selModel.description || 'Asigna un CI existente a cada rol (opcional si está vacío).'}
                </p>
                {(selModel.nodes || []).map((node) => {
                  const b = bindings[node.role];
                  return (
                    <div key={node.role} className="sd-role-row">
                      <div className="sd-role-info">
                        <span className="sd-role-name">
                          {node.role} {node.required ? <em className="sd-required">*</em> : null}
                        </span>
                        {node.ci_type && <span className="sd-role-type">{TYPE_LABELS[node.ci_type] || node.ci_type}</span>}
                      </div>
                      {b ? (
                        <div className="sd-role-chip">
                          <span className="sd-node-dot" style={{ background: TYPE_COLORS[b.ci_type] || '#64748b' }} />
                          <span className="sd-role-chip-name">{b.name}</span>
                          <span className="sd-role-chip-type">{TYPE_LABELS[b.ci_type] || b.ci_type}</span>
                          <button className="sd-role-chip-x" onClick={() => clearCi(node.role)} title="Quitar">✕</button>
                        </div>
                      ) : (
                        <div className="sd-role-search">
                          <input
                            value={queries[node.role] || ''}
                            onChange={(e) => searchRole(node.role, node, e.target.value)}
                            placeholder="Buscar CI existente…"
                          />
                          {searching[node.role] && <div className="sd-hint">Buscando…</div>}
                          {(results[node.role] || []).length > 0 && (
                            <div className="sd-role-results">
                              {(results[node.role] || []).map((ci, i) => (
                                <button key={`${ci.ci_type}:${ci.ci_id}:${i}`} className="sd-role-result" onClick={() => pickCi(node.role, ci)}>
                                  <span className="sd-node-dot" style={{ background: TYPE_COLORS[ci.ci_type] || '#64748b' }} />
                                  {ci.name}
                                  <span className="sd-role-chip-type">{TYPE_LABELS[ci.ci_type] || ci.ci_type}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div className="sd-modal-actions">
          <button className="sd-cancel-btn" onClick={onClose}>Cancelar</button>
          <button
            className="sd-save-btn"
            disabled={!selModel || applying}
            onClick={apply}
          >
            {applying ? 'Aplicando…' : 'Aplicar plantilla'}
          </button>
        </div>
      </div>
    </div>
  );
}
